import type { ConversationMode } from '@shared/conversation-types';

/**
 * Resolve the conversation mode for the active surface (017).
 *
 * The `/scoping` route (`routeMode === 'scoping'`) is authoritative and is
 * derived SYNCHRONOUSLY from the path by the caller, so a lagging
 * `lifecycle.mode` — which only catches up to the route one effect tick later —
 * or the single-project `'update'` lobby shortcut can never leak the previous
 * surface's mode (e.g. `'update'` from `/overview`, or a recovered session)
 * into a scoping session's `startSession`/voice call.
 *
 * Off the scoping route the existing precedence holds: the single-project lobby
 * shortcut forces `'update'`, otherwise the lifecycle mode wins (which also
 * reflects a server-driven `cloud_mode_change`, e.g. an interview handoff).
 */
export function resolveSurfaceMode(
  routeMode: ConversationMode,
  hasSingleProject: boolean,
  lifecycleMode: ConversationMode,
): ConversationMode {
  if (routeMode === 'scoping') return 'scoping';
  return hasSingleProject ? 'update' : lifecycleMode;
}

/**
 * Whether the GLOBAL conversation provider should PROACTIVELY (auto-)resume a
 * conversation — i.e. fire an agent-first turn — on mount/navigation.
 *
 * 017 boot intents: the global provider is **lazy**. Plain navigation to home,
 * update/overview, or a scoping route must NEVER auto-send a turn just because a
 * project + prior conversation id exist (the live regression: messages firing
 * when you open a project page). Scoping's greet is driven at the PAGE level
 * (`ProjectScopingPage` → `ChatThread proactiveStart`), not here.
 *
 * The ONE exception (rule 4) is an explicit cross-page continuation: when the
 * LLM emits a load-page action and the destination receives the handoff
 * (`continuationRequested === true`), the provider proactively continues the
 * conversation on the new page. SCOPING is never resumed by the global provider
 * even on a continuation — its proactive greet is owned by ProjectScopingPage
 * (`ChatThread proactiveStart`), so resuming here too would double-fire `/start`
 * (D9 / FR-003). We still require a resolved project + conversation id.
 */
export function shouldResumeConversation(args: {
  mode: ConversationMode;
  hasProjectId: boolean;
  hasConversationId: boolean;
  projectStatus?: unknown;
  continuationRequested?: boolean;
}): boolean {
  if (!args.continuationRequested) return false; // lazy on plain navigation
  // Scoping is page-level only (D9 / FR-003) — never globally resumed.
  if (args.mode === 'scoping') return false;
  if (!args.hasProjectId || !args.hasConversationId) return false;
  return true;
}
